import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { LogIn, LogOut, UserRound } from 'lucide-react'
import { Link, useLocation } from 'react-router'
import { authEnabled } from '@/auth/config'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { headerButton } from './headerButton'

/**
 * Sign-in in the header: "Log in" (Auth0's sign-in page, then back to this page), or once
 * signed in the user's picture and name, opening a menu with Account and Log out.
 * Hidden when sign-in isn't configured, and while the session is being restored.
 */
export function UserMenu() {
  const { isLoading, isAuthenticated, user, loginWithRedirect, logout } =
    useAuth0()
  const { pathname, search } = useLocation()
  const [open, setOpen] = useState(false)
  if (!authEnabled || isLoading) return null

  if (!isAuthenticated) {
    return (
      <Button
        variant="ghost"
        className={headerButton}
        aria-label="Log in"
        onClick={() =>
          void loginWithRedirect({
            appState: { returnTo: pathname + search },
          })
        }
      >
        <LogIn aria-hidden="true" />
        <span className="hidden sm:inline">Log in</span>
      </Button>
    )
  }

  const name = user?.name || user?.email || 'Account'
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={headerButton}
          aria-label={`Account menu for ${name}`}
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              referrerPolicy="no-referrer"
              className="size-7 rounded-full"
            />
          ) : (
            <UserRound aria-hidden="true" />
          )}
          <span className="hidden max-w-40 truncate sm:inline">{name}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-64 flex-col gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium">{name}</p>
          {user?.email && user.email !== name && (
            <p className="truncate text-sm text-muted-foreground">
              {user.email}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Button variant="ghost" className="justify-start" asChild>
            <Link to="/account" onClick={() => setOpen(false)}>
              <UserRound aria-hidden="true" />
              Account
            </Link>
          </Button>
          <Button
            variant="ghost"
            className="justify-start"
            onClick={() =>
              void logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          >
            <LogOut aria-hidden="true" />
            Log out
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
